import React from 'react';
import { Database, TrendingUp, Users, Building2, Sparkles } from 'lucide-react';

/**
 * EnrichIQ product mockup — a fake dashboard rendered in pure JSX/CSS.
 * Mimics the LeadIQ Pro live-demo screenshot: browser chrome, stats row,
 * a highlighted lead card with dual scores + tech-stack chips.
 */
const EnrichIQMockup = () => {
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-mk-lg bg-white ring-1 ring-slate-200">
      {/* Browser chrome */}
      <div className="flex items-center justify-between bg-navy-deep px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-4 inline-flex items-center gap-1.5 rounded-md bg-brand-pink/90 text-white text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Live demo
          </span>
        </div>
        <div className="text-slate-400 text-[11px] font-bold tracking-[0.14em]">enrichiq.5dbs.io</div>
        <div className="w-16" />
      </div>

      {/* Dashboard body */}
      <div className="bg-white p-6 sm:p-8">
        <p className="text-body-muted text-[10.5px] font-bold tracking-[0.16em] uppercase">
          Workspace · Enrichment
        </p>
        <div className="mt-2 flex items-end justify-between gap-4">
          <h3 className="text-navy font-black text-[26px] sm:text-[30px] leading-none tracking-tight">
            Lead intelligence,<br />
            <span className="text-brand-blue">at scale.</span>
          </h3>
          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-brand-blue/10 text-brand-blue text-[10.5px] font-bold uppercase tracking-[0.14em] px-3 py-1">
            <Sparkles size={11} strokeWidth={2.5} />
            Live web research
          </span>
        </div>

        {/* Stats row */}
        <div className="mt-6 grid grid-cols-4 gap-3">
          <div className="rounded-xl bg-brand-soft p-3">
            <p className="text-body-muted text-[9.5px] font-bold uppercase tracking-[0.14em]">Total leads</p>
            <p className="mt-1 text-navy text-[24px] font-black leading-none">5</p>
          </div>
          <div className="rounded-xl bg-brand-soft p-3">
            <p className="text-body-muted text-[9.5px] font-bold uppercase tracking-[0.14em]">High prospects</p>
            <p className="mt-1 text-emerald-600 text-[24px] font-black leading-none">3</p>
          </div>
          <div className="rounded-xl bg-brand-soft p-3">
            <p className="text-body-muted text-[9.5px] font-bold uppercase tracking-[0.14em]">Review</p>
            <p className="mt-1 text-amber-500 text-[24px] font-black leading-none">2</p>
          </div>
          <div className="rounded-xl bg-brand-soft p-3">
            <p className="text-body-muted text-[9.5px] font-bold uppercase tracking-[0.14em]">Avg score</p>
            <p className="mt-1 text-brand-blue text-[24px] font-black leading-none">72</p>
          </div>
        </div>

        {/* Featured lead card */}
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-mk-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-brand-blue to-brand-blue-light text-white flex items-center justify-center shrink-0">
                <Building2 size={16} strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className="text-navy text-[14px] font-bold truncate">Northwind Cloud</p>
                <p className="text-body-muted text-[11.5px] truncate">northwind.example · Cloud Infrastructure</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex flex-col items-center rounded-lg bg-[#D4922A]/10 text-[#D4922A] px-3 py-1.5 leading-none">
                <span className="text-[8.5px] font-bold tracking-[0.14em] uppercase">Opp</span>
                <span className="text-[16px] font-black">88</span>
              </span>
              <span className="inline-flex flex-col items-center rounded-lg bg-emerald-100 text-emerald-700 px-3 py-1.5 leading-none">
                <span className="text-[8.5px] font-bold tracking-[0.14em] uppercase">Fit</span>
                <span className="text-[16px] font-black">92</span>
              </span>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {['AWS', 'Kubernetes', 'HubSpot', 'Datadog', 'Terraform'].map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-md bg-slate-100 text-navy text-[10.5px] font-bold px-2 py-0.5"
              >
                <Database size={10} strokeWidth={2.5} />
                {t}
              </span>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11.5px]">
            <div className="flex items-start gap-2">
              <TrendingUp size={13} className="mt-0.5 text-brand-blue shrink-0" strokeWidth={2.4} />
              <span className="text-body-muted">
                <span className="text-navy font-bold">Series B closed</span> — $42M led by Redpoint
              </span>
            </div>
            <div className="flex items-start gap-2">
              <Users size={13} className="mt-0.5 text-brand-blue shrink-0" strokeWidth={2.4} />
              <span className="text-body-muted">
                <span className="text-navy font-bold">Hiring 3 engineers</span> · RevOps role open
              </span>
            </div>
          </div>
        </div>

        {/* Second small lead (muted) */}
        <div className="mt-3 rounded-xl border border-slate-100 bg-white p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-slate-100 text-body-muted flex items-center justify-center shrink-0">
              <Building2 size={14} strokeWidth={2.4} />
            </div>
            <div className="min-w-0">
              <p className="text-navy text-[13px] font-bold truncate">Meridian Analytics</p>
              <p className="text-body-muted text-[11px] truncate">Fintech · SaaS · 200 employees</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex flex-col items-center rounded-lg bg-slate-100 text-body-muted px-2.5 py-1 leading-none">
              <span className="text-[8px] font-bold tracking-[0.14em] uppercase">Opp</span>
              <span className="text-[13px] font-black">54</span>
            </span>
            <span className="inline-flex flex-col items-center rounded-lg bg-slate-100 text-body-muted px-2.5 py-1 leading-none">
              <span className="text-[8px] font-bold tracking-[0.14em] uppercase">Fit</span>
              <span className="text-[13px] font-black">31</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrichIQMockup;
